<template>
  <section class="form-limited-width">
    <base-form-group
      :label="$t('personInfo.3508')"
      :is-invalid="$v.localPatient.surname.$error"
      data-testid="base-group-surname"
      :errors="{
        required: !$v.localPatient.surname.required,
        allowSymbols: !$v.localPatient.surname.allowSymbols
      }"
    >
      <base-input
        :is-invalid="$v.localPatient.surname.$error"
        data-testid="input-surname"
        v-model="localPatient.surname"
        name="surname"
        @blur="$v.localPatient.surname.$touch()"
      >
      </base-input>
    </base-form-group>
    <base-form-group
      :label="$t('personInfo.2678')"
      :is-invalid="$v.localPatient.name.$error"
      data-testid="base-group-name"
      :errors="{
        required: !$v.localPatient.name.required,
        allowSymbols: !$v.localPatient.name.allowSymbols
      }"
    >
      <base-input
        :is-invalid="$v.localPatient.name.$error"
        data-testid="input-name"
        v-model="localPatient.name"
        name="name"
        @blur="$v.localPatient.name.$touch()"
      >
      </base-input>
    </base-form-group>
    <base-form-group
      :label="$t('personInfo.547')"
      :is-invalid="$v.localPatient.middle_name.$error"
      data-testid="base-group-middle-name"
      :errors="{
        allowSymbols: !$v.localPatient.middle_name.allowSymbols
      }"
    >
      <base-input
        :is-invalid="$v.localPatient.middle_name.$error"
        data-testid="input-middle-name"
        v-model="localPatient.middle_name"
        name="middleName"
        @blur="$v.localPatient.middle_name.$touch()"
      >
      </base-input>
    </base-form-group>
    <base-form-group
      :label="$t('personInfo.dateOfBirth')"
      :is-invalid="$v.localPatient.birth_date.$error"
      data-testid="base-group-birth-date"
      :errors="{
        required: !$v.localPatient.birth_date.required && $v.localPatient.birth_date.$dirty
      }"
    >
      <base-calendar-wrap>
        <date-input
          v-model="localPatient.birth_date"
          @input="$v.localPatient.birth_date.$touch()"
          id="input-birth-date"
          :max-date="nowFormatServer"
          :allow-input="true"
        ></date-input>
      </base-calendar-wrap>
    </base-form-group>

    <base-form-group
      :label="$t('personInfo.6786')"
      :is-invalid="$v.localPatient.gender.$error"
      data-testid="base-group-gender"
      :errors="{
        required: !$v.localPatient.gender.required
      }"
    >
      <div class="form-check-inline w-100">
        <div class="custom-control custom-radio">
          <input
            class="custom-control-input"
            type="radio"
            name="exampleRadios"
            id="male"
            value="0"
            v-model="localPatient.gender"
          />
          <label class="custom-control-label" data-testid="rb-label-male" for="male">{{ $t('personInfo.571') }}</label>
        </div>
        <div class="custom-control custom-radio">
          <input
            class="custom-control-input"
            type="radio"
            name="exampleRadios"
            id="female"
            value="1"
            v-model="localPatient.gender"
          />
          <label class="custom-control-label" data-testid="rb-label-female" for="female">{{ $t('personInfo.8312') }}</label>
        </div>
      </div>
    </base-form-group>

    <base-form-group-for-two-inputs
      :labelFirst="$t('personInfo.1927')"
      :labelSecond="$t('personInfo.8637')"
      :is-invalid="$v.localPatient.height.$error || $v.localPatient.weight.$error"
      :errors="{
        invalidData: !$v.localPatient.height.invalidData || !$v.localPatient.weight.invalidData
      }"
    >
      <template #leftInput>
        <base-input
          :is-invalid="$v.localPatient.height.$error"
          v-model="localPatient.height"
          data-testid="input-height"
          name="height"
          type="text"
          :isNumber="true"
          @blur="$v.localPatient.height.$touch()"
        >
        </base-input>
      </template>
      <template #rightInput>
        <base-input
          :is-invalid="$v.localPatient.weight.$error"
          v-model="localPatient.weight"
          data-testid="input-weight"
          name="weight"
          type="text"
          :isNumber="true"
          @blur="$v.localPatient.weight.$touch()"
        >
        </base-input>
      </template>
    </base-form-group-for-two-inputs>

    <base-form-group
      :label="$t('personInfo.phone')"
      :is-invalid="$v.localPatient.phone.$error"
      :errors="{
        likePhone: !$v.localPatient.phone.likePhone,
        phoneOverlong: !$v.localPatient.phone.phoneOverlong
      }"
    >
      <base-input v-model="localPatient.phone" data-testid="input-phone" name="phone" type="tel" @blur="$v.localPatient.phone.$touch()">
      </base-input>
    </base-form-group>
    <base-form-group
      :label="$t('general.address')"
      :is-invalid="$v.localPatient.address.$error"
      :errors="{
        likePhone: !$v.localPatient.address.allowSymbols
      }"
    >
      <base-textarea v-model="localPatient.address" data-testid="textarea-address" name="address" @blur="$v.localPatient.address.$touch()">
      </base-textarea>
    </base-form-group>

    <div class="d-flex mt-4 justify-content-between">
      <button type="button" data-testid="btn-save" class="btn btn-primary" @click="submit">
        <span v-if="saving" class="spinner-border spinner-border-sm"></span>{{ $t('button.saveAndContinue') }}
      </button>
      <button type="button" class="btn btn-secondary ml-3" @click="cancel" v-if="isCancelBtn">
        <span v-if="saving" class="spinner-border spinner-border-sm"></span>{{ $t('button.cancel') }}
      </button>
    </div>
  </section>
</template>

<script>
import { required } from 'vuelidate/lib/validators'
import {
  allowSymbols,
  likePhone,
  phoneOverlong,
  heightMoreThanWeight,
  heightLess300,
  weightLess300
} from '@/modules/validators'
import BaseCalendarWrap from '@/base/BaseCalendarWrap.vue'
import { nowMoment } from '@/mixins/now-moment'

export default {
  name: 'PatientForm',
  components: {
    DateInput: () => import('@/plugins/DateInput.vue'),
    BaseFormGroup: () => import('@/base/BaseFormGroup.vue'),
    BaseFormGroupForTwoInputs: () => import('@/base/BaseFormGroupForTwoInputs.vue'),
    BaseInput: () => import('@/base/BaseInput.vue'),
    BaseTextarea: () => import('@/base/BaseTextarea.vue'),
    BaseCalendarWrap: BaseCalendarWrap
  },
  mixins: [nowMoment],
  model: {
    prop: 'patient'
  },
  props: {
    patient: {
      type: Object,
      required: true
    },
    saving: {
      type: Boolean
    },
    isCancelBtn: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data: () => ({
    localPatient: null,
    gender: null
  }),
  validations: {
    localPatient: {
      surname: {
        required,
        allowSymbols
      },
      name: {
        required,
        allowSymbols
      },
      middle_name: {
        allowSymbols
      },
      birth_date: {
        required
      },
      gender: {
        required
      },
      phone: {
        likePhone,
        phoneOverlong
      },
      address: {
        allowSymbols
      },
      height: {
        heightLess300,
        heightMoreThanWeight
      },
      weight: {
        weightLess300,
        heightMoreThanWeight
      }
    }
  },
  watch: {
    localPatient: {
      deep: true,
      handler() {
        this.$emit('input', this.localPatient)
      }
    }
  },
  created() {
    this.localPatient = Object.assign({}, this.patient)
  },
  methods: {
    submit() {
      this.$v.$touch()
      if (!this.$v.$invalid) {
        this.$emit('submit')
        this.$v.$reset()
      }
    },
    cancel() {
      this.$emit('cancel')
    }
  }
}
</script>
